import { createApp as createVueApp, defineAsyncComponent } from 'vue'
import emitter from 'tiny-emitter/instance'

import Modal from './VueComponents/modal.vue'
import CartTimer from './VueComponents/cart-timer.vue'
import LoadingSpinner from './VueComponents/loading-spinner.vue'
import DoorSalesControls from './VueComponents/DoorSales/door-sales-controls.vue'

import { formatMoney } from 'accounting-js'
import { setupAxios } from './axiosSetup'
import { createUiVariantPlugin } from './VuePlugins/uiVariant'

export function currencyFormat(value) {
    if (value === '-') return value;
    return formatMoney(value, '$', 2, ',', ',');
}

export function formatFreeOrCurrency(value) {
    return value === 0 ? 'Free' : currencyFormat(value);
}

const addGlobalComponentsToVue = (app) => {
    app.component('modal', Modal)
    app.component('cart-timer', CartTimer)
    app.component('door-sales-controls', DoorSalesControls)
    app.component('loading-spinner', LoadingSpinner)
    // Async so the chunk is fetched only by pages that actually render the tag — one
    // registration serves Event/Extras/Checkout v2 without weighing down online pages.
    app.component('door-sales-v2-controls', defineAsyncComponent(() =>
        import(/* webpackChunkName: "door-sales-v2" */ './VueComponents/DoorSalesV2/DoorSalesControlsV2.vue')))
}

const addPostHogErrorTracking = (app) => {
    // Vue's development build rethrows to the page only without an errorHandler, which Playwright's pageerror guards need.
    if (!window.oztixPostHog) return

    app.config.errorHandler = (err, instance, info) => {
        console.error(err)
        window.oztixPostHog?.captureException?.(err, {
            // Vue's production build passes the error-reference URL, whose fragment is the only part
            // that names the error; before_send strips fragments from anything URL-shaped.
            vue_error_info: String(info).split('#').pop(),
            // A single-file component that declares no name gets __name from the Vue SFC compiler.
            vue_component: instance?.$?.type?.name ?? instance?.$?.type?.__name ?? instance?.$options?.name ?? null,
        })
    }
}

const createEventBus = () => {
    const EventBus = {
        $on: (...args) => emitter.on(...args),
        $once: (...args) => emitter.once(...args),
        $off: (...args) => emitter.off(...args),
        $emit: (...args) => emitter.emit(...args),
    };
    window.EventBus = EventBus;
    return EventBus;
};

export const EventBus = createEventBus()

export function createApp(component) {
    setupAxios();
    const app = createVueApp(component);
    app.use(createUiVariantPlugin());
    addGlobalComponentsToVue(app);
    addPostHogErrorTracking(app);
    return app;
}

export const getDefaultStripeOptions = () => ({
    apiVersion: '2024-06-20'
});